import { localDB } from './pouchdb-config';
import cuid from 'cuid';
import { countryCurrencyMap } from './init-data';
import { getGooglePayConfig } from '../components/googlepay-config';

const paymentService = {
  // Process payment using Google Pay
  processGooglePayPayment: (orderData, customerInfo) => {
    return new Promise((resolve, reject) => {
      try {
        // Check if Google Pay is available
        if (!window.google || !window.google.payments) {
          reject({
            success: false,
            error: 'Google Pay not available'
          });
          return;
        }

        // Get Google Pay configuration
        const config = getGooglePayConfig(process.env.REACT_APP_GOOGLEPAY_ENV || 'TEST');
        
        const paymentsClient = new window.google.payments.api.PaymentsClient({
          environment: config.environment
        });

        const paymentDataRequest = {
          apiVersion: 2,
          apiVersionMinor: 0,
          allowedPaymentMethods: [{
            type: 'UPI',
            parameters: {
              payeeVpa: config.upi.payeeVpa,
              payeeName: config.upi.payeeName,
              referenceUrl: window.location.origin,
              mcc: config.upi.mcc,
              tr: orderData.orderId || Date.now().toString()
            },
            tokenizationSpecification: {
              type: 'DIRECT'
            }
          }],
          merchantInfo: config.merchantInfo,
          transactionInfo: {
            totalPriceStatus: config.transactionInfo.totalPriceStatus,
            totalPrice: orderData.totalAmount.toString(),
            currencyCode: config.transactionInfo.currencyCode,
            countryCode: config.transactionInfo.countryCode
          }
        };

        paymentsClient.loadPaymentData(paymentDataRequest)
          .then(paymentData => {
            // Save payment to database
            const paymentRecord = {
              _id: 'gpay_' + Date.now(),
              type: 'payment',
              method: 'googlepay',
              orderId: orderData.orderId,
              customerId: customerInfo.customerId,
              amount: orderData.totalAmount,
              currency: 'INR',
              status: 'paid',
              paymentData: paymentData,
              createdAt: new Date().toISOString()
            };

            localDB.put(paymentRecord)
              .then(() => {
                resolve({
                  success: true,
                  paymentId: paymentRecord._id,
                  paymentData: paymentRecord
                });
              })
              .catch(err => {
                reject({
                  success: false,
                  error: 'Failed to save payment'
                });
              });
          })
          .catch(err => {
            reject({
              success: false,
              error: err.statusMessage || 'Google Pay failed'
            });
          });
      } catch (error) {
        reject({
          success: false,
          error: 'Google Pay initialization failed'
        });
      }
    });
  },

  // Process subscription payment using Razorpay
  processSubscriptionPayment: (subscriptionData, customerInfo) => {
    return new Promise((resolve, reject) => {
      try {
        const amountInSmallestUnit = Math.round(subscriptionData.monthlyPrice * 100);
        
        const options = {
          key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_1DP5mmOlF5G5ag',
          amount: amountInSmallestUnit,
          currency: 'INR',
          name: 'Organic Greens Hub',
          description: `Subscription: ${subscriptionData.planName}`,
          image: 'https://i.imgur.com/n5tjHFD.png',
          handler: function(response) {
            const paymentId = response.razorpay_payment_id;
            const paymentRecord = {
              _id: 'sub_payment_' + paymentId,
              type: 'subscription_payment',
              razorpay_payment_id: paymentId,
              subscriptionId: subscriptionData.subscriptionId,
              customerId: customerInfo.customerId,
              amount: subscriptionData.monthlyPrice,
              currency: 'INR',
              planType: subscriptionData.planType,
              status: 'paid',
              createdAt: new Date().toISOString()
            };

            // Save payment record to backend API directly — avoids PouchDB conflict issues
            const token = localStorage.getItem('token');
            const apiUrl = process.env.REACT_APP_API_URL || '';
            fetch(`${apiUrl}/api/${paymentRecord._id}`, {
              method: 'PUT',
              headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {})
              },
              body: JSON.stringify(paymentRecord)
            }).catch(() => {}); // fire and forget — don't block redirect

            // Redirect immediately regardless of payment record save
            const successUrl = `/payment/success?paymentId=${paymentId}&subscriptionType=subscription&planType=${subscriptionData.planType || 'subscription'}`;
            window.location.href = successUrl;
          },
          prefill: {
            name: customerInfo.name || '',
            email: customerInfo.email || '',
            contact: customerInfo.phone || ''
          },
          theme: {
            color: '#133c0b'
          },
          modal: {
            ondismiss: function() {
              reject({
                success: false,
                error: 'Payment cancelled by user'
              });
            }
          }
        };
        
        const rzp = new window.Razorpay(options);
        rzp.open();
        
      } catch (error) {
        reject({
          success: false,
          error: 'Failed to initiate payment'
        });
      }
    });
  },
  processRazorpayPayment: (orderData, customerInfo) => {
    return new Promise((resolve, reject) => {
      try {
        // Store customer email in session storage for later use in email confirmation
        if (customerInfo && customerInfo.email) {
          sessionStorage.setItem('customerEmail', customerInfo.email);
          if (customerInfo.name) {
            sessionStorage.setItem('customerName', customerInfo.name);
          }
        }
        
        // Use appropriate currency based on customer location
        const currencyCode = orderData.currency || 'INR';
        
        // Ensure we have a valid amount (minimum 1 INR = 100 paise)
        let amount = orderData.totalAmount;
        if (!amount || amount < 1) amount = 1;
        
        // Convert amount to paise - multiply by 100
        const amountInSmallestUnit = Math.round(amount * 100);
        
        // Set a timeout to handle cases where the Razorpay modal might get stuck
        const paymentTimeout = setTimeout(() => {
          reject({
            success: false,
            error: 'Payment timed out. Please try again.'
          });
        }, 60000); // 60 seconds timeout
        
        // Check if Razorpay is loaded
        if (typeof window.Razorpay === 'undefined') {
          console.error('Razorpay SDK not loaded');
          
          // Try to load Razorpay script dynamically
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = false;
          document.body.appendChild(script);
          
          // Wait for script to load using setTimeout
          setTimeout(() => {
            // Check again
            if (typeof window.Razorpay === 'undefined') {
              clearTimeout(paymentTimeout);
              reject({
                success: false,
                error: 'Payment gateway not available. Please try again later.'
              });
            } else {
              // Create and open Razorpay checkout after script is loaded
              createRazorpayInstance();
            }
          }, 2000);
          return;
        }
        
        // Function to create and open Razorpay instance
        function createRazorpayInstance() {
          // Live Razorpay key
          const options = {
            key: process.env.REACT_APP_RAZORPAY_KEY_ID || 'rzp_test_1DP5mmOlF5G5ag', // Use environment variable
            amount: amountInSmallestUnit,
            currency: currencyCode,
            name: 'Organic Greens Hub',
            description: `Payment for order ${orderData.orderId || 'items'}`,
            image: 'https://i.imgur.com/n5tjHFD.png', // Using a public image hosting service
            handler: function(response) {
              // Clear the timeout since payment completed
              clearTimeout(paymentTimeout);
              
              // Save payment details to PouchDB
              const paymentData = {
                _id: 'payment_' + response.razorpay_payment_id,
                type: 'payment',
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature,
                orderId: orderData.orderId,
                customerId: customerInfo.customerId,
                amount: orderData.totalAmount,
                currency: currencyCode,
                items: orderData.items,
                status: 'paid',
                createdAt: new Date().toISOString()
              };
              
              // Save via API
              localDB.put(paymentData)
                .then(() => {
                  console.log('Payment saved successfully:', paymentData);
                  resolve({
                    success: true,
                    paymentId: response.razorpay_payment_id,
                    paymentData
                  });
                })
                .catch(err => {
                  console.error('Error saving payment:', err);
                  reject({
                    success: false,
                    error: 'Failed to save payment details'
                  });
                });
            },
            prefill: {
              name: customerInfo.name || '',
              email: customerInfo.email || '',
              contact: customerInfo.phone || ''
            },
            notes: {
              customerId: customerInfo.customerId,
              address: customerInfo.address
            },
            theme: {
              color: '#133c0b'
            },
            modal: {
              ondismiss: function() {
                // Clear the timeout since modal was dismissed
                clearTimeout(paymentTimeout);
                
                reject({
                  success: false,
                  error: 'Payment cancelled by user'
                });
              },
              escape: true, // Allow closing with Escape key
              backdropclose: true // Allow closing by clicking outside
            }
          };
          
          try {
            // Create Razorpay checkout
            const rzp = new window.Razorpay(options);
            
            // Add event handlers for better error handling
            rzp.on('payment.failed', function(response) {
              console.error('Payment failed:', response.error);
              // Clear the timeout since payment failed
              clearTimeout(paymentTimeout);
              reject({
                success: false,
                error: response.error.description || 'Payment failed'
              });
            });
            
            // Open the checkout modal
            console.log('Opening Razorpay checkout with options:', options);
            rzp.open();
          } catch (rzpError) {
            console.error('Error creating Razorpay instance:', rzpError);
            clearTimeout(paymentTimeout);
            reject({
              success: false,
              error: 'Failed to initialize payment gateway: ' + rzpError.message
            });
          }
        }
        
        // Call the function to create Razorpay instance
        createRazorpayInstance();
        
      } catch (error) {
        console.error('Error initiating Razorpay payment:', error);
        reject({
          success: false,
          error: 'Failed to initiate payment'
        });
      }
    });
  },
  
  // Get payment history for a customer
  getPaymentHistory: async (customerId) => {
    try {
      const result = await localDB.find({
        selector: {
          type: 'payment',
          customerId: customerId
        }
      });
      
      return result.docs || [];
    } catch (error) {
      console.error('Error getting payment history:', error);
      throw error;
    }
  },
  
  // Get payment details by ID
  getPaymentById: async (paymentId) => {
    try {
      return await localDB.get(paymentId);
    } catch (error) {
      console.error(`Error getting payment with ID ${paymentId}:`, error);
      throw error;
    }
  }
};

export default paymentService;